import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Sparkles, X, Check, Ban, Loader2, TrendingUp, ExternalLink,
  RotateCcw, ArrowRightCircle, Plus,
} from "lucide-react";
import {
  api,
  type ClusterView,
  type ClusterProposalView,
  type SuggestionStatus,
  type SuggestionView,
} from "../../lib/api.js";
import { CoverageBadge } from "../../components/ui/CoverageBadge.js";

/**
 * Cluster-expansion dialog. Surfaces (per cluster):
 *
 *   1. "Find more" button → POST /clusters/:id/expand (DFS + LLM + classifier)
 *   2. Status tabs (Pending / Accepted / Rejected) so operators can revisit
 *      decisions made earlier — rejected suggestions can be Reopen'd.
 *   3. Per-row affordances:
 *      - Accept (creates keyword in this cluster)
 *      - Move to ▾ (creates keyword in a DIFFERENT cluster — useful when the
 *        classifier flags a better fit, or operator just disagrees)
 *      - Reject (sticks across re-runs; can be undone via Reopen)
 *   4. "Better fit: X (Y% conf)" pill when the classifier picked a different
 *      EXISTING cluster than this expansion was run against.
 *   5. Proposed-new-cluster section at the top, listing every distinct
 *      idSuggestion the classifier proposed across ALL pending suggestions
 *      (not just this cluster's). One click creates the new cluster + accepts
 *      the seeding suggestions into it.
 */
export function ExpandClusterDialog({ cluster, onClose }: { cluster: ClusterView; onClose: () => void }) {
  const qc = useQueryClient();
  const [banner, setBanner] = useState<{ kind: "ok" | "warn" | "error"; text: string } | null>(null);
  const [tab, setTab] = useState<SuggestionStatus>("pending");

  const suggestionsQuery = useQuery({
    queryKey: ["clusters", cluster.id, "suggestions", tab],
    queryFn: () => api.listSuggestions(cluster.id, tab),
  });
  const allClustersQuery = useQuery({
    queryKey: ["pipeline", "clusters"],
    queryFn: () => api.listClusters(),
  });
  const proposalsQuery = useQuery({
    queryKey: ["cluster-proposals"],
    queryFn: () => api.listClusterProposals(),
  });

  const expandMut = useMutation({
    mutationFn: () => api.expandCluster(cluster.id, {}),
    onSuccess: (r) => {
      setBanner({ kind: "ok", text: r.summary });
      qc.invalidateQueries({ queryKey: ["clusters", cluster.id, "suggestions"] });
      qc.invalidateQueries({ queryKey: ["cluster-proposals"] });
    },
    onError: (e: Error) => setBanner({ kind: "error", text: e.message }),
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["clusters", cluster.id, "suggestions"] });
    qc.invalidateQueries({ queryKey: ["cluster-proposals"] });
    qc.invalidateQueries({ queryKey: ["pipeline"] });
  };

  const otherClusters = useMemo(
    () => (allClustersQuery.data?.clusters ?? []).filter((c) => c.id !== cluster.id),
    [allClustersQuery.data, cluster.id],
  );

  // ── Multi-select + bulk actions ───────────────────────────────────────────
  const suggestions = suggestionsQuery.data?.suggestions ?? [];
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulk, setBulk] = useState<{ busy: boolean; done: number; total: number }>({ busy: false, done: 0, total: 0 });
  // Clear selection when switching tabs (the visible set changes).
  useEffect(() => { setSelected(new Set()); }, [tab]);

  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  const allSelected = suggestions.length > 0 && selected.size === suggestions.length;
  const toggleSelectAll = () =>
    setSelected(allSelected ? new Set() : new Set(suggestions.map((s) => s.id)));

  const runBulk = async (action: { type: "accept" | "reject" | "reopen"; targetClusterId?: string; verb: string }) => {
    const ids = [...selected];
    if (ids.length === 0 || bulk.busy) return;
    setBanner(null);
    setBulk({ busy: true, done: 0, total: ids.length });
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        if (action.type === "accept") await api.acceptSuggestion(cluster.id, id, action.targetClusterId ? { targetClusterId: action.targetClusterId } : {});
        else if (action.type === "reject") await api.rejectSuggestion(cluster.id, id);
        else await api.reopenSuggestion(cluster.id, id);
        ok++;
      } catch { fail++; }
      setBulk((b) => ({ ...b, done: b.done + 1 }));
    }
    setBulk({ busy: false, done: 0, total: 0 });
    setSelected(new Set());
    invalidateAll();
    setBanner({ kind: fail ? "warn" : "ok", text: `${ok} ${action.verb}${fail ? ` · ${fail} failed` : ""}.` });
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-ink/20 backdrop-blur-[2px]" onClick={onClose} aria-hidden />
      <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto px-4 py-10">
        <div className="w-full max-w-3xl rounded-xl bg-white shadow-2xl">
          <header className="flex items-start justify-between gap-4 border-b border-line px-6 py-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-2xs font-semibold uppercase tracking-wider text-brand">
                <Sparkles className="h-3.5 w-3.5" />
                Cluster expansion
              </div>
              <h2 className="mt-0.5 truncate text-lg font-semibold">
                {cluster.nameEn}
                <span className="ml-2 text-xs font-normal text-muted" dir="rtl">{cluster.nameHe}</span>
              </h2>
              <p className="mt-1 text-xs text-muted">
                {cluster.currentArticleCount} keywords in cluster
                {cluster.targetArticleCount ? ` · target ${cluster.targetArticleCount}` : ""}
              </p>
            </div>
            <button onClick={onClose} className="btn-ghost shrink-0" title="Close">
              <X className="h-4 w-4" />
            </button>
          </header>

          {banner && (
            <div className={`mx-6 mt-4 rounded-md px-3 py-2 text-xs ${
              banner.kind === "ok" ? "bg-accent/10 text-accent" :
              banner.kind === "warn" ? "bg-warn/10 text-warn" :
              "bg-danger/10 text-danger"
            }`}>
              {banner.text}
            </div>
          )}

          <section className="border-b border-line px-6 py-4">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <h3 className="text-sm font-semibold">Find more keywords</h3>
                <p className="mt-0.5 text-xs text-muted">
                  Pulls DFS related keywords for the cluster's seeds, runs Claude expansion, then classifies each suggestion against the full cluster map. ≤ $0.15/run.
                </p>
              </div>
              <button
                onClick={() => { setBanner(null); expandMut.mutate(); }}
                disabled={expandMut.isPending}
                className="btn-primary shrink-0 text-sm"
              >
                {expandMut.isPending ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Expanding…
                  </>
                ) : (
                  <>
                    <Sparkles className="h-3.5 w-3.5" />
                    Find more
                  </>
                )}
              </button>
            </div>
            {expandMut.data && (
              <details className="mt-3 rounded-md border border-line bg-canvas-deep/40 p-3 text-xs">
                <summary className="cursor-pointer font-medium">What this batch addresses</summary>
                <p className="mt-2 text-muted">{expandMut.data.gapsFilled}</p>
                <div className="mt-2 font-mono text-2xs text-muted">
                  DFS: {expandMut.data.dfsRawCount} raw → {expandMut.data.dfsCandidateCount} candidates · ${expandMut.data.dfsCost.account.toFixed(4)}
                  {" · "}LLM: {expandMut.data.llm.modelUsed} · in {expandMut.data.llm.inputTokens} / out {expandMut.data.llm.outputTokens}
                  {expandMut.data.classifier && (
                    <>
                      <br />Classifier: {expandMut.data.classifier.suggestionsClassified} classified ·{" "}
                      {expandMut.data.classifier.crossClusterFits} cross-cluster ·{" "}
                      {expandMut.data.classifier.newClusterProposals} new-cluster proposals
                    </>
                  )}
                </div>
                {expandMut.data.warnings.length > 0 && (
                  <ul className="mt-2 list-disc pl-4 text-warn">
                    {expandMut.data.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                )}
              </details>
            )}
          </section>

          {/* New-cluster proposals — span across all clusters, not just this one. */}
          {proposalsQuery.data && proposalsQuery.data.count > 0 && (
            <section className="border-b border-line bg-violet-50/40 px-6 py-4">
              <h3 className="text-sm font-semibold text-violet-900">
                Proposed new clusters
                <span className="ml-2 text-xs font-normal text-violet-700">({proposalsQuery.data.count})</span>
              </h3>
              <p className="mt-0.5 text-xs text-violet-700">
                Suggestions that don't fit any existing cluster. Approving creates the cluster and accepts the seeding keywords into it.
              </p>
              <ul className="mt-3 space-y-2">
                {proposalsQuery.data.proposals.map((p) => (
                  <ProposalRow
                    key={p.idSuggestion}
                    proposal={p}
                    onChanged={() => { invalidateAll(); }}
                    onError={(msg) => setBanner({ kind: "error", text: msg })}
                  />
                ))}
              </ul>
            </section>
          )}

          <section className="px-6 py-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold">Suggestions</h3>
              <div className="flex gap-1">
                {(["pending", "accepted", "rejected"] as SuggestionStatus[]).map((t) => (
                  <button
                    key={t}
                    onClick={() => setTab(t)}
                    className={
                      tab === t
                        ? "rounded-full bg-brand px-3 py-1 text-2xs font-medium uppercase tracking-wider text-white"
                        : "rounded-full bg-line px-3 py-1 text-2xs font-medium uppercase tracking-wider text-muted hover:bg-line/80 hover:text-ink"
                    }
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {suggestionsQuery.isPending ? (
              <div className="text-xs text-muted">Loading suggestions…</div>
            ) : suggestionsQuery.data && suggestionsQuery.data.suggestions.length === 0 ? (
              <div className="rounded-md border border-dashed border-line bg-canvas px-3 py-6 text-center text-xs text-muted">
                {tab === "pending"
                  ? "No pending suggestions. Click Find more to generate some."
                  : tab === "accepted"
                  ? "No accepted suggestions yet."
                  : "No rejected suggestions yet."}
              </div>
            ) : (
              <>
                {/* Bulk action bar */}
                <div className="mb-2 flex flex-wrap items-center gap-2 rounded-md border border-line bg-canvas px-3 py-2 text-xs">
                  <label className="flex cursor-pointer items-center gap-1.5 font-medium">
                    <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                    {selected.size > 0 ? `${selected.size} selected` : "Select all"}
                  </label>
                  {selected.size > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-muted">·</span>
                      {tab === "pending" && (
                        <>
                          <button onClick={() => runBulk({ type: "accept", verb: "accepted" })} disabled={bulk.busy} className="btn btn-primary text-xs">
                            <Check className="h-3 w-3" /> Accept
                          </button>
                          <button onClick={() => runBulk({ type: "reject", verb: "rejected" })} disabled={bulk.busy} className="btn btn-ghost text-xs">
                            <Ban className="h-3 w-3" /> Reject
                          </button>
                          <select
                            value=""
                            disabled={bulk.busy || otherClusters.length === 0}
                            onChange={(e) => { if (e.target.value) runBulk({ type: "accept", targetClusterId: e.target.value, verb: "moved" }); }}
                            className="input py-1 text-xs"
                          >
                            <option value="">Move to…</option>
                            {otherClusters.map((c) => <option key={c.id} value={c.id}>{c.nameEn}</option>)}
                          </select>
                        </>
                      )}
                      {tab === "rejected" && (
                        <button onClick={() => runBulk({ type: "reopen", verb: "reopened" })} disabled={bulk.busy} className="btn btn-ghost text-xs">
                          <RotateCcw className="h-3 w-3" /> Reopen
                        </button>
                      )}
                      {bulk.busy && (
                        <span className="inline-flex items-center gap-1 text-muted">
                          <Loader2 className="h-3 w-3 animate-spin" /> {bulk.done}/{bulk.total}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <ul className="space-y-2">
                  {suggestions.map((s) => (
                    <SuggestionRow
                      key={s.id}
                      suggestion={s}
                      clusterId={cluster.id}
                      otherClusters={otherClusters}
                      selected={selected.has(s.id)}
                      onToggleSelect={() => toggleSelect(s.id)}
                      onChanged={() => invalidateAll()}
                      onError={(msg) => setBanner({ kind: "error", text: msg })}
                    />
                  ))}
                </ul>
              </>
            )}
          </section>

          <footer className="flex justify-end gap-2 border-t border-line bg-canvas px-6 py-3">
            <button onClick={onClose} className="btn-ghost text-sm">Close</button>
          </footer>
        </div>
      </div>
    </>
  );
}

function ProposalRow({
  proposal,
  onChanged,
  onError,
}: {
  proposal: ClusterProposalView;
  onChanged: () => void;
  onError: (msg: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const onApprove = async () => {
    setBusy(true);
    try {
      const r = await api.approveClusterProposal(proposal.idSuggestion);
      if (r.skipped.length > 0) {
        onError(`Created cluster but ${r.skipped.length} suggestion(s) failed to seed`);
      }
      onChanged();
    } catch (e) {
      onError((e as Error).message);
    } finally { setBusy(false); }
  };

  return (
    <li className="rounded-lg border border-violet-200 bg-white p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold text-violet-900">{proposal.nameEn}</span>
            <span className="text-xs text-violet-700" dir="rtl">{proposal.nameHe}</span>
            <span className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-mono text-violet-700">
              {proposal.idSuggestion}
            </span>
          </div>
          <p className="mt-1 text-xs text-muted" dir="auto">
            {proposal.reasons[0] ?? ""}
            {proposal.reasons.length > 1 && ` (+${proposal.reasons.length - 1} more)`}
          </p>
          <div className="mt-2 flex flex-wrap gap-1">
            {proposal.keywords.map((k, i) => (
              <span key={i} className="rounded-full bg-violet-50 px-2 py-0.5 text-[11px] text-violet-700 ring-1 ring-violet-200" dir="auto">
                {k}
              </span>
            ))}
          </div>
          <div className="mt-1.5 text-[11px] text-muted">
            {proposal.suggestionIds.length} seed{proposal.suggestionIds.length === 1 ? "" : "s"} ·
            avg {Math.round(proposal.averageConfidence * 100)}% conf
          </div>
        </div>
        <button
          onClick={onApprove}
          disabled={busy}
          className="btn btn-primary shrink-0 text-xs"
          title={`Create cluster "${proposal.idSuggestion}" and accept ${proposal.suggestionIds.length} seeding keyword${proposal.suggestionIds.length === 1 ? "" : "s"}`}
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
          Approve cluster
        </button>
      </div>
    </li>
  );
}

function SuggestionRow({
  suggestion,
  clusterId,
  otherClusters,
  selected,
  onToggleSelect,
  onChanged,
  onError,
}: {
  suggestion: SuggestionView;
  clusterId: string;
  otherClusters: ClusterView[];
  selected: boolean;
  onToggleSelect: () => void;
  onChanged: () => void;
  onError: (msg: string) => void;
}) {
  const [busy, setBusy] = useState<"accept" | "reject" | "move" | "reopen" | null>(null);
  const [moveOpen, setMoveOpen] = useState(false);

  const accept = async (targetClusterId?: string) => {
    setBusy(targetClusterId ? "move" : "accept");
    setMoveOpen(false);
    try {
      await api.acceptSuggestion(clusterId, suggestion.id, targetClusterId ? { targetClusterId } : {});
      onChanged();
    } catch (e) {
      onError((e as Error).message);
    } finally { setBusy(null); }
  };
  const onReject = async () => {
    setBusy("reject");
    try {
      await api.rejectSuggestion(clusterId, suggestion.id);
      onChanged();
    } catch (e) {
      onError((e as Error).message);
    } finally { setBusy(null); }
  };
  const onReopen = async () => {
    setBusy("reopen");
    try {
      await api.reopenSuggestion(clusterId, suggestion.id);
      onChanged();
    } catch (e) {
      onError((e as Error).message);
    } finally { setBusy(null); }
  };

  const sourceBadge =
    suggestion.source === "dfs_related" ? { label: "DFS", color: "bg-emerald-100 text-emerald-700" } :
    suggestion.source === "dfs_suggestions" ? { label: "DFS sug", color: "bg-emerald-100 text-emerald-700" } :
    suggestion.source === "llm_expansion" ? { label: "AI", color: "bg-violet-100 text-violet-700" } :
    suggestion.source === "paa_mining" ? { label: "PAA", color: "bg-sky-100 text-sky-700" } :
    { label: "Trends", color: "bg-amber-100 text-amber-700" };

  const betterFitCluster = otherClusters.find((c) => c.id === suggestion.suggestedClusterId);

  return (
    <li className={`rounded-lg border p-3 ${selected ? "border-brand bg-brand-50/40" : "border-line bg-white"}`}>
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          className="mt-1 shrink-0"
          title="Select for bulk action"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-semibold" dir="auto">{suggestion.keyword}</span>
            <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${sourceBadge.color}`}>
              {sourceBadge.label}
            </span>
            {suggestion.isPillarCandidate && (
              <span className="shrink-0 rounded bg-brand/10 px-1.5 py-0.5 text-[10px] font-medium text-brand">
                pillar
              </span>
            )}
            <CoverageBadge coverage={suggestion.coverage} />
            {betterFitCluster && (
              <button
                onClick={() => accept(betterFitCluster.id)}
                disabled={busy !== null}
                title={`Move to ${betterFitCluster.nameEn} (${Math.round((suggestion.classifierConfidence ?? 0) * 100)}% conf)`}
                className="inline-flex shrink-0 items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 hover:bg-amber-200"
              >
                <ArrowRightCircle className="h-3 w-3" />
                Better fit: {betterFitCluster.nameEn} ({Math.round((suggestion.classifierConfidence ?? 0) * 100)}%)
              </button>
            )}
          </div>
          {suggestion.rationale && (
            <p className="mt-1 text-xs text-muted" dir="auto">{suggestion.rationale}</p>
          )}
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted">
            {suggestion.searchVolume !== null && (
              <span className="inline-flex items-center gap-0.5">
                <TrendingUp className="h-3 w-3" />
                {suggestion.searchVolume.toLocaleString()} vol
              </span>
            )}
            {suggestion.cpc !== null && <span>${suggestion.cpc.toFixed(2)} CPC</span>}
            {suggestion.keywordDifficulty !== null && <span>KD {suggestion.keywordDifficulty}</span>}
            {suggestion.estimatedIntent && <span>{suggestion.estimatedIntent}</span>}
            {suggestion.funnelStage && <span>{suggestion.funnelStage}</span>}
            {suggestion.recommendedPriority && <span>{suggestion.recommendedPriority}</span>}
            {suggestion.sourceSeed && (
              <span className="inline-flex items-center gap-0.5">
                <ExternalLink className="h-3 w-3" />
                from "{suggestion.sourceSeed}"
              </span>
            )}
          </div>
          {suggestion.status !== "pending" && suggestion.reviewedNotes && (
            <p className="mt-1 text-[11px] italic text-muted">Note: {suggestion.reviewedNotes}</p>
          )}
        </div>

        <div className="flex shrink-0 flex-col gap-1">
          {suggestion.status === "pending" ? (
            <>
              <div className="flex gap-1">
                <button
                  onClick={() => accept()}
                  disabled={busy !== null}
                  title="Accept into this cluster"
                  className="btn btn-primary text-xs"
                >
                  {busy === "accept" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                  Accept
                </button>
                <div className="relative">
                  <button
                    onClick={() => setMoveOpen((o) => !o)}
                    disabled={busy !== null || otherClusters.length === 0}
                    title="Accept into a different cluster"
                    className="btn btn-ghost text-xs"
                  >
                    {busy === "move" ? <Loader2 className="h-3 w-3 animate-spin" /> : <ArrowRightCircle className="h-3 w-3" />}
                    Move to ▾
                  </button>
                  {moveOpen && (
                    <div className="absolute right-0 z-10 mt-1 max-h-64 w-56 overflow-y-auto rounded-md border border-line bg-white shadow-lg">
                      {otherClusters.map((c) => (
                        <button
                          key={c.id}
                          onClick={() => accept(c.id)}
                          className="block w-full px-3 py-1.5 text-left text-xs hover:bg-canvas"
                        >
                          <div className="font-medium">{c.nameEn}</div>
                          <div className="text-2xs text-muted" dir="rtl">{c.nameHe}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={onReject}
                disabled={busy !== null}
                title="Reject — sticks across re-runs"
                className="btn btn-ghost text-xs"
              >
                {busy === "reject" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Ban className="h-3 w-3" />}
                Reject
              </button>
            </>
          ) : suggestion.status === "rejected" ? (
            <button
              onClick={onReopen}
              disabled={busy !== null}
              title="Reopen — flips back to pending so you can accept or move it"
              className="btn btn-primary text-xs"
            >
              {busy === "reopen" ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
              Reopen
            </button>
          ) : (
            <div className="text-2xs uppercase tracking-wider text-accent">
              <Check className="inline h-3 w-3" /> accepted
            </div>
          )}
        </div>
      </div>
    </li>
  );
}
